import React, { useEffect, useMemo, useState } from 'react';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { venueAPI, trainingAPI, userAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, AlertCircle, GraduationCap, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errorMessage';

const statusBadge = (status) => {
  if (status === 'expired') return 'bg-[#FF3B30]/20 text-[#FF3B30]';
  if (status === 'expiring_soon') return 'bg-[#FFB300]/20 text-[#FFB300]';
  return 'bg-[#00E676]/20 text-[#00E676]';
};

const TrainingPage = () => {
  const { user } = useAuthStore();
  const [venues, setVenues] = useState([]);
  const [venueUsers, setVenueUsers] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [records, setRecords] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    certificate_name: '',
    expiry_date: '',
    document_url: '',
  });

  const canManage = hasMinimumRole(user, ROLES.MANAGER);

  const visibleVenues = useMemo(() => {
    if (hasMinimumRole(user, ROLES.OPS)) return venues;
    return venues.filter((v) => (user?.venue_ids || []).includes(v.id));
  }, [venues, user]);

  const fetchVenueUsers = async (venueId) => {
    if (!venueId) {
      setVenueUsers([]);
      return;
    }
    const res = await userAPI.listByVenue(venueId, true);
    setVenueUsers(res?.data || []);
  };

  const fetchRecords = async (venueId) => {
    if (!venueId) {
      setRecords([]);
      setGrouped([]);
      return;
    }

    const [recordsRes, groupedRes] = await Promise.all([
      trainingAPI.list(venueId),
      trainingAPI.grouped(venueId),
    ]);
    setRecords(recordsRes?.data || []);
    setGrouped(groupedRes?.data?.sections || []);
  };

  const initialize = async () => {
    setLoading(true);
    try {
      const venuesRes = await venueAPI.list();
      const venueList = venuesRes?.data || [];
      setVenues(venueList);
      const firstVenue = venueList[0]?.id || '';
      setSelectedVenueId(firstVenue);
      if (firstVenue) {
        await Promise.all([fetchVenueUsers(firstVenue), fetchRecords(firstVenue)]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleVenueChange = async (event) => {
    const nextVenueId = event.target.value;
    setSelectedVenueId(nextVenueId);
    setLoading(true);
    setFormData({ user_id: '', certificate_name: '', expiry_date: '', document_url: '' });
    try {
      await Promise.all([fetchVenueUsers(nextVenueId), fetchRecords(nextVenueId)]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!selectedVenueId) {
      toast.error('Select a venue first');
      return;
    }

    setSaving(true);
    try {
      await trainingAPI.create({
        venue_id: selectedVenueId,
        user_id: formData.user_id,
        certificate_name: formData.certificate_name,
        expiry_date: formData.expiry_date,
        document_url: formData.document_url || null,
      });
      toast.success('Training record created and staff notified');
      setFormData({ user_id: '', certificate_name: '', expiry_date: '', document_url: '' });
      await fetchRecords(selectedVenueId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Delete this training record?')) return;
    try {
      await trainingAPI.delete(recordId);
      toast.success('Training record deleted');
      await fetchRecords(selectedVenueId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            Training Records
          </h1>
          <p className="text-[#A1A1AA] mt-1">Assign certificates by staff name and track expiry cleanly.</p>
        </div>
      </div>

      <div className="card-rtb">
        <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Venue</Label>
        <select
          value={selectedVenueId}
          onChange={handleVenueChange}
          className="w-full mt-2 bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
        >
          {visibleVenues.map((venue) => (
            <option key={venue.id} value={venue.id}>{venue.name}</option>
          ))}
        </select>
      </div>

      {canManage && (
        <form onSubmit={handleCreate} className="card-rtb space-y-4">
          <h2 className="font-heading text-lg font-bold text-white uppercase">Add Record</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">User Name *</Label>
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                className="w-full mt-1 bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
                required
              >
                <option value="">Select staff member</option>
                {venueUsers.map((venueUser) => (
                  <option key={venueUser.id} value={venueUser.id}>
                    {venueUser.name} ({venueUser.role})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Certificate *</Label>
              <Input
                className="input-rtb mt-1"
                value={formData.certificate_name}
                onChange={(e) => setFormData({ ...formData, certificate_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Expiry Date *</Label>
              <Input
                type="date"
                className="input-rtb mt-1"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Document URL</Label>
              <Input
                className="input-rtb mt-1"
                value={formData.document_url}
                onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
              />
            </div>
          </div>
          <Button type="submit" className="btn-rtb-primary" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Upload Training Record
          </Button>
        </form>
      )}

      <div className="card-rtb">
        <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Grouped Training View</h2>
        {grouped.length === 0 ? (
          <div className="text-center py-10 text-[#A1A1AA]">
            <AlertCircle className="w-10 h-10 mx-auto text-[#3F3F46] mb-3" />
            No training records found for this venue.
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map((section) => (
              <div key={section.department} className="bg-[#1E1E1E] border border-[#3F3F46] rounded-sm p-4">
                <h3 className="font-heading text-base text-white uppercase mb-3">{section.department}</h3>
                <div className="space-y-3">
                  {section.certificates.map((cert) => (
                    <div key={cert.certificate_name} className="bg-[#111111] border border-[#3F3F46] rounded-sm p-3">
                      <p className="text-white font-medium text-sm mb-2">{cert.certificate_name}</p>
                      <div className="flex flex-wrap gap-2">
                        {cert.holders.map((holder) => (
                          <span key={holder.record_id} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${statusBadge(holder.status)}`}>
                            <Users className="w-3 h-3" />
                            {holder.user_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-rtb">
        <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Raw Records</h2>
        {records.length === 0 ? (
          <p className="text-[#A1A1AA]">No records.</p>
        ) : (
          <div className="space-y-3">
            {records.map((record) => (
              <div key={record.id} className="bg-[#1E1E1E] border border-[#3F3F46] rounded-sm p-4 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-md bg-[#27272A] flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-[#FF6600]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{record.certificate_name}</p>
                    <p className="text-[#A1A1AA] text-xs mt-1 truncate">Holder: {record.user_name || record.user_id}</p>
                    <p className="text-[#A1A1AA] text-xs">Expires: {record.expiry_date}</p>
                    {record.document_url && (
                      <a href={record.document_url} target="_blank" rel="noreferrer" className="text-[#FF6600] text-xs hover:underline">
                        View document
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${statusBadge(record.status || 'valid')}`}>
                    {(record.status || 'valid').replace('_', ' ')}
                  </span>
                  {canManage && (
                    <Button variant="secondary" className="btn-rtb-secondary px-2" onClick={() => handleDelete(record.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingPage;
